import { useState, useEffect, useCallback } from 'react';
import {
  PlusCircle, MinusCircle, Search, Package, AlertTriangle, RefreshCw, History, Store, Warehouse, ShieldAlert
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { inventoryAPI, warehousesAPI, storesAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';
import { useAuth } from '@/contexts/AuthContext';
import { AdjustmentHistory, ADJUSTMENT_REASONS } from '@/components/hq/AdjustmentHistory';

const StockAdjustmentsPage = () => {
  const { user } = useAuth();
  const isWarehouseUser = user?.entity_type === 'warehouse';
  const isSuperAdmin = user?.role_name === 'HQ Super Admin';

  const [locationType, setLocationType] = useState('warehouse');
  const [warehouses, setWarehouses] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(isWarehouseUser ? user?.entity_id : '');
  const [selectedStore, setSelectedStore] = useState('');
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [adjustmentQty, setAdjustmentQty] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const { toast } = useToast();

  const isStoreMode = locationType === 'store';
  const selectedLocationId = isStoreMode ? selectedStore : selectedWarehouse;
  const canAdjust = isStoreMode ? isSuperAdmin : true;

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      if (isWarehouseUser) {
        setWarehouses([{ id: user?.entity_id, name: 'My Warehouse' }]);
        setSelectedWarehouse(user?.entity_id);
      } else {
        const [whRes, stRes] = await Promise.all([
          warehousesAPI.list({ is_active: true }),
          storesAPI.list({ is_active: true, limit: 500 })
        ]);
        const whs = whRes.data || [];
        const sts = Array.isArray(stRes.data) ? stRes.data : (stRes.data?.items || stRes.data?.stores || []);
        setWarehouses(whs);
        setStores(sts);
        if (whs.length > 0) setSelectedWarehouse((cur) => cur || whs[0].id);
        if (sts.length > 0) setSelectedStore((cur) => cur || sts[0].id);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch locations', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, isWarehouseUser, user?.entity_id]);

  const fetchStock = useCallback(async () => {
    if (!selectedLocationId) return;
    setLoading(true);
    try {
      if (isStoreMode) {
        const res = await inventoryAPI.getHQStoreStockDetail(selectedLocationId, { group_by: 'batch', include_zero: true, limit: 2000 });
        setStockItems((res.data?.items || []).map((i) => ({
          ...i,
          product_name: i.product_name || i.name,
          product_sku: i.sku,
          uom: '',
        })));
      } else {
        const res = await inventoryAPI.getWarehouseStock(selectedLocationId, { view: 'active' });
        setStockItems(res.data?.items || []);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to fetch stock', variant: 'destructive' });
      setStockItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, isStoreMode, toast]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);
  useEffect(() => { if (selectedLocationId) fetchStock(); }, [selectedLocationId, fetchStock]);

  const handleOpenAdjustment = (item, type) => {
    setSelectedItem(item);
    setAdjustmentType(type);
    setAdjustmentQty('');
    setReasonCategory('');
    setAdjustmentReason('');
    setAdjustDialogOpen(true);
  };

  const handleAdjust = async () => {
    const qty = parseFloat(adjustmentQty);
    if (!qty || qty <= 0) {
      toast({ title: 'Error', description: 'Enter valid quantity', variant: 'destructive' });
      return;
    }
    if (!reasonCategory) {
      toast({ title: 'Error', description: 'Select a reason category', variant: 'destructive' });
      return;
    }
    if (!adjustmentReason.trim()) {
      toast({ title: 'Error', description: 'Enter remarks for the adjustment', variant: 'destructive' });
      return;
    }
    if (adjustmentType === 'remove' && qty > selectedItem.quantity) {
      toast({ title: 'Error', description: 'Cannot remove more than available quantity', variant: 'destructive' });
      return;
    }

    setAdjusting(true);
    try {
      await inventoryAPI.stockAdjustment({
        product_id: selectedItem.product_id,
        ...(isStoreMode ? { store_id: selectedStore } : { warehouse_id: selectedWarehouse }),
        adjustment_qty: qty,
        adjustment_type: adjustmentType,
        reason_category: reasonCategory,
        reason: adjustmentReason.trim(),
        batch_number: selectedItem.batch_number || undefined
      });
      toast({ title: 'Success', description: `Stock ${adjustmentType === 'add' ? 'added' : 'removed'} successfully` });
      setAdjustDialogOpen(false);
      fetchStock();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to adjust stock', variant: 'destructive' });
    } finally {
      setAdjusting(false);
    }
  };

  const handleViewHistory = async (item) => {
    setSelectedItem(item);
    setMovementsLoading(true);
    setHistoryDialogOpen(true);
    try {
      const response = await inventoryAPI.getStockMovements(item.product_id, {
        warehouse_id: selectedLocationId
      });
      setMovements(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch movements', variant: 'destructive' });
    } finally {
      setMovementsLoading(false);
    }
  };

  const filteredItems = stockItems.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return item.product_name?.toLowerCase().includes(s) || item.product_sku?.toLowerCase().includes(s);
  });

  const selectedLocation = isStoreMode
    ? stores.find(s => s.id === selectedStore)
    : warehouses.find(w => w.id === selectedWarehouse);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="stock-adjustments-page">
      <Toaster />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Stock Adjustments</h1>
          <p className="text-gray-500">Manually adjust stock quantities for physical count corrections</p>
        </div>
        <Button variant="outline" onClick={fetchStock} className="border-gray-300" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs defaultValue="adjust">
        <TabsList className="mb-4" data-testid="adjustments-tabs">
          <TabsTrigger value="adjust" data-testid="tab-adjust">Adjust Stock</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Adjustment History</TabsTrigger>
        </TabsList>

        <TabsContent value="adjust">
          <Card className="bg-white border-gray-200 shadow-sm mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                {!isWarehouseUser && (
                  <div>
                    <Label className="text-gray-600">Location</Label>
                    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden mt-2 block" data-testid="location-type-switch">
                      <button
                        type="button"
                        onClick={() => setLocationType('warehouse')}
                        className={`px-4 py-2 text-sm flex items-center gap-1.5 transition-colors ${!isStoreMode ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        data-testid="location-type-warehouse"
                      >
                        <Warehouse className="w-4 h-4" /> Warehouse
                      </button>
                      <button
                        type="button"
                        onClick={() => setLocationType('store')}
                        className={`px-4 py-2 text-sm flex items-center gap-1.5 transition-colors ${isStoreMode ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        data-testid="location-type-store"
                      >
                        <Store className="w-4 h-4" /> Store
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex-1 max-w-xs">
                  <Label className="text-gray-600">Select {isStoreMode ? 'Store' : 'Warehouse'}</Label>
                  {isStoreMode ? (
                    <Select value={selectedStore} onValueChange={setSelectedStore}>
                      <SelectTrigger className="mt-2 bg-gray-50 border-gray-300" data-testid="store-select">
                        <SelectValue placeholder="Select store..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {stores.map(st => (
                          <SelectItem key={st.id} value={st.id}>{st.name} ({st.code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                      <SelectTrigger className="mt-2 bg-gray-50 border-gray-300" data-testid="warehouse-select">
                        <SelectValue placeholder="Select warehouse..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {warehouses.map(wh => (
                          <SelectItem key={wh.id} value={wh.id}>{wh.name}{wh.code ? ` (${wh.code})` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search product..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-300"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {selectedLocation && (
                <div className={`mt-4 p-4 rounded-lg ${isStoreMode ? 'bg-emerald-50' : 'bg-teal-50'}`}>
                  <p className={`font-semibold ${isStoreMode ? 'text-emerald-800' : 'text-teal-800'}`}>{selectedLocation.name}</p>
                  <p className={`text-sm ${isStoreMode ? 'text-emerald-600' : 'text-teal-600'}`}>{selectedLocation.code} • {filteredItems.length} stock rows</p>
                </div>
              )}

              {isStoreMode && !isSuperAdmin && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2 text-sm text-amber-800" data-testid="store-adjust-readonly-note">
                  <ShieldAlert className="w-4 h-4" /> Only HQ Super Admin can adjust store stock. You can view stock and history.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800">Stock Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading...</div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No stock items found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 border-gray-200">
                        <TableHead className="text-gray-700 font-semibold">Product</TableHead>
                        <TableHead className="text-gray-700 font-semibold">Batch</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-center">Current Qty</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-center">Reorder</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-right">Cost</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-right">Value</TableHead>
                        <TableHead className="text-gray-700 font-semibold text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredItems.map((item, index) => (
                        <TableRow key={`${item.product_id}-${item.batch_number || index}`} className="border-gray-200 hover:bg-gray-50" data-testid={`stock-row-${index}`}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-teal-600">{item.product_sku}</p>
                          </TableCell>
                          <TableCell className="text-gray-600">{item.batch_number || 'Default'}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-bold ${item.quantity <= 0 ? 'text-red-600' : item.is_low_stock ? 'text-amber-600' : 'text-gray-800'}`}>
                              {item.quantity} {item.uom}
                            </span>
                          </TableCell>
                          <TableCell className="text-center text-gray-500">{item.reorder_level}</TableCell>
                          <TableCell className="text-right text-gray-600">₹{(item.cost_price || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium text-gray-800">₹{(item.stock_value || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleOpenAdjustment(item, 'add')}
                                className="h-8 px-2 text-emerald-600 hover:bg-emerald-50" title="Add Stock"
                                disabled={!canAdjust} data-testid={`add-stock-${index}`}>
                                <PlusCircle className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleOpenAdjustment(item, 'remove')}
                                className="h-8 px-2 text-red-600 hover:bg-red-50" title="Remove Stock"
                                disabled={!canAdjust || item.quantity <= 0} data-testid={`remove-stock-${index}`}>
                                <MinusCircle className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleViewHistory(item)}
                                className="h-8 px-2 text-blue-600 hover:bg-blue-50" title="View History" data-testid={`history-${index}`}>
                                <History className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200 mt-6">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-800">About Stock Adjustments</p>
                  <ul className="text-sm text-blue-700 mt-1 space-y-1">
                    <li>• Use adjustments for physical count corrections after stock-taking</li>
                    <li>• Store stock corrections are restricted to HQ Super Admin</li>
                    <li>• Every adjustment is logged with reason category, remarks, before/after qty and user</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <AdjustmentHistory
                lockedLocationType={isWarehouseUser ? 'warehouse' : undefined}
                lockedLocationId={isWarehouseUser ? user?.entity_id : undefined}
                stores={stores}
                warehouses={warehouses}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Adjustment Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              {adjustmentType === 'add' ? <PlusCircle className="w-5 h-5 text-emerald-500" /> : <MinusCircle className="w-5 h-5 text-red-500" />}
              {adjustmentType === 'add' ? 'Add Stock' : 'Remove Stock'}
              <Badge variant="outline" className="ml-auto capitalize">{isStoreMode ? 'Store' : 'Warehouse'}</Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800">{selectedItem.product_name}</p>
                <p className="text-sm text-teal-600">{selectedItem.product_sku}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedLocation?.name}</p>
                {selectedItem.batch_number && <Badge variant="outline" className="mt-2">{selectedItem.batch_number}</Badge>}
                <p className="text-sm text-gray-500 mt-2">
                  Current Qty: <span className="font-bold text-gray-800">{selectedItem.quantity} {selectedItem.uom}</span>
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">{adjustmentType === 'add' ? 'Quantity to Add' : 'Quantity to Remove'} *</Label>
                <Input type="number" min="1" max={adjustmentType === 'remove' ? selectedItem.quantity : undefined}
                  value={adjustmentQty} onChange={(e) => setAdjustmentQty(e.target.value)} placeholder="Enter quantity"
                  className="bg-gray-50 border-gray-300" data-testid="adjustment-qty-input" />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Reason *</Label>
                <Select value={reasonCategory} onValueChange={setReasonCategory}>
                  <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="adjustment-reason-select">
                    <SelectValue placeholder="Select reason category..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {ADJUSTMENT_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Remarks *</Label>
                <Textarea value={adjustmentReason} onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder={adjustmentType === 'add' ? 'e.g., Physical count - found 5 extra units' : 'e.g., Stock-taking variance - 3 units missing'}
                  className="bg-gray-50 border-gray-300" rows={3} data-testid="adjustment-reason-input" />
              </div>

              {adjustmentQty && parseFloat(adjustmentQty) > 0 && (
                <div className={`p-3 rounded ${adjustmentType === 'add' ? 'bg-emerald-50' : 'bg-red-50'}`} data-testid="adjustment-preview">
                  <p className={adjustmentType === 'add' ? 'text-emerald-700' : 'text-red-700'}>
                    New Qty: <span className="font-bold">
                      {adjustmentType === 'add' ? selectedItem.quantity + parseFloat(adjustmentQty) : selectedItem.quantity - parseFloat(adjustmentQty)} {selectedItem.uom}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)} className="border-gray-300">Cancel</Button>
            <Button onClick={handleAdjust}
              disabled={adjusting || !adjustmentQty || !reasonCategory || !adjustmentReason.trim()}
              className={adjustmentType === 'add' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}
              data-testid="confirm-adjustment-btn">
              {adjusting ? 'Processing...' : (adjustmentType === 'add' ? 'Add Stock' : 'Remove Stock')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Movement History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <History className="w-5 h-5 text-blue-500" />
              Stock Movement History - {selectedItem?.product_name}
            </DialogTitle>
          </DialogHeader>

          {movementsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : movements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No movement history found</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-700">Date</TableHead>
                    <TableHead className="text-gray-700">Type</TableHead>
                    <TableHead className="text-gray-700">From</TableHead>
                    <TableHead className="text-gray-700">To</TableHead>
                    <TableHead className="text-gray-700 text-right">Qty</TableHead>
                    <TableHead className="text-gray-700">Reference</TableHead>
                    <TableHead className="text-gray-700">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((mov, idx) => (
                    <TableRow key={idx} className="border-gray-200">
                      <TableCell className="text-gray-600 text-sm">{formatDate(mov.created_at)}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{mov.movement_type?.replace('_', ' ')}</Badge></TableCell>
                      <TableCell className="text-gray-600 text-sm capitalize">{mov.from_location_type || '-'}</TableCell>
                      <TableCell className="text-gray-600 text-sm capitalize">{mov.to_location_type}</TableCell>
                      <TableCell className="text-right font-medium">{mov.quantity}</TableCell>
                      <TableCell className="text-teal-600 text-sm">{mov.reference_number || '-'}</TableCell>
                      <TableCell className="text-gray-500 text-sm max-w-xs truncate">{mov.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)} className="border-gray-300">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockAdjustmentsPage;
